import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import { atom, useAtom } from 'jotai'

import { Modal } from '@makyoapp/makyo-ui'

import { THUMB_SQUARE } from '@/shared/constants'

type ModalState = {
  show: boolean
  value: string | null
  title: string
  width: number
}

const modalAtom = atom<ModalState>({
  show: false,
  value: null,
  title: '',
  width: 350,
})

interface PreviewModalProps {
  state: ModalState
  onClose: () => void
}

function PreviewModal({ state, onClose }: PreviewModalProps) {
  const { value, title, width = 350 } = state
  if (!value) return null
  return (
    <Modal show={true} onHide={onClose}>
      <Modal.Header onCloseClick={onClose}>
        <Trans>{title}</Trans>
      </Modal.Header>
      <Modal.Body>
        <div id='preview-container' className='p-2 rounded-md'>
          <img
            src={value}
            alt={title}
            style={{ width }}
            className='h-full touch-none select-none'
            onError={(e) => {
              e.currentTarget.src = THUMB_SQUARE
            }}
          />
        </div>
      </Modal.Body>
    </Modal>
  )
}

/**
 * This component is used to preview images, barcodes, and QR codes in a modal.
 * It listens for the `preview-modal-open` event and displays the image, barcode, or QR code in a modal.
 * The modal is closed when the `preview-modal-close` event is triggered.
 *
 * @returns A modal component that displays the image, barcode, or QR code.
 */
export default function GridPreviewModal() {
  const [modalState, setModalState] = useAtom(modalAtom)

  useEffect(() => {
    const handleEvent = (event: any) => {
      const { value, title, width } = event.detail
      setModalState({ show: true, value, title, width })
    }
    document.addEventListener('preview-modal-open', handleEvent)
    return () => {
      document.removeEventListener('preview-modal-open', handleEvent)
    }
  }, [setModalState])

  const closeModal = () => {
    setModalState({ show: false, value: null, title: '', width: 350 })
  }

  if (modalState.show && modalState.value) {
    return <PreviewModal onClose={closeModal} state={modalState} />
  }

  return null
}
